import React from "react";
import { Pill, Stethoscope, HeartPulse, Users, ShieldPlus, Scissors } from "lucide-react";
import { careTopics } from "../mock";

const ICONS = [Users, Stethoscope, Pill, HeartPulse, ShieldPlus, Scissors];

export default function WhyMatters() {
  return (
    <section className="relative px-4 md:px-8 py-20 md:py-28 bg-[#f0edfa] molecule-bg overflow-hidden">
      <div className="mx-auto max-w-[1400px]">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="eyebrow">Your Health</span>
          <h2 className="section-title">Why regular care matters in Australia</h2>
          <p className="text-[#4a4a63] mt-5 leading-relaxed">
            Australia has some of the highest skin cancer rates in the world and rural communities face unique health challenges. Regular check-ups and preventive care save lives.
          </p>
        </div>

        {/* Layout: 3 cols left / center circle / 3 cols right */}
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-12 items-center">
          {/* Left column */}
          <div className="space-y-14">
            {careTopics.slice(0, 3).map((t, i) => {
              const Icon = ICONS[i];
              return (
                <div key={t.title} className="flex items-start gap-4 lg:justify-end lg:text-right">
                  <div className="lg:order-2 shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#dfeaff] to-[#c9dbff] grid place-items-center shadow-sm">
                    <Icon className="w-6 h-6 text-[#1a2464]" />
                  </div>
                  <div className="lg:order-1 max-w-xs">
                    <h3 className="font-bold text-[#1a2464] mb-2">{t.title}</h3>
                    <p className="text-[#4a4a63] text-sm leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Center circular graphic */}
          <div className="relative w-[340px] h-[340px] md:w-[420px] md:h-[420px] mx-auto">
            <CircleDiagram />
          </div>

          {/* Right column */}
          <div className="space-y-14">
            {careTopics.slice(3, 6).map((t, i) => {
              const Icon = ICONS[i + 3];
              return (
                <div key={t.title} className="flex items-start gap-4">
                  <div className="shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#dfeaff] to-[#c9dbff] grid place-items-center shadow-sm">
                    <Icon className="w-6 h-6 text-[#1a2464]" />
                  </div>
                  <div className="max-w-xs">
                    <h3 className="font-bold text-[#1a2464] mb-2">{t.title}</h3>
                    <p className="text-[#4a4a63] text-sm leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function CircleDiagram() {
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full">
      <defs>
        <radialGradient id="core" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#5b6bd6" />
          <stop offset="100%" stopColor="#1a2464" />
        </radialGradient>
      </defs>
      {/* concentric circles */}
      <circle cx="200" cy="200" r="185" fill="none" stroke="#c9d4f5" strokeWidth="1.5" strokeDasharray="3 6" opacity="0.6" />
      <circle cx="200" cy="200" r="140" fill="none" stroke="#a8b7ee" strokeWidth="1.5" opacity="0.5" />
      <circle cx="200" cy="200" r="95" fill="none" stroke="#8b9ce6" strokeWidth="1.5" opacity="0.55" />

      {/* orbiting dots */}
      <circle cx="200" cy="15" r="6" fill="#1a2464" opacity="0.7" />
      <circle cx="375" cy="180" r="6" fill="#3dc9b3" />
      <circle cx="200" cy="385" r="6" fill="#1a2464" opacity="0.7" />
      <circle cx="25" cy="215" r="6" fill="#3dc9b3" />

      {/* text curves */}
      <path id="curveTop" d="M 90 200 A 110 110 0 0 1 310 200" fill="transparent" />
      <path id="curveBottom" d="M 90 200 A 110 110 0 0 0 310 200" fill="transparent" />
      <text fill="#1a2464" fontSize="13" fontWeight="600" letterSpacing="2">
        <textPath href="#curveTop" startOffset="50%" textAnchor="middle">FAMILY CARE</textPath>
      </text>
      <text fill="#1a2464" fontSize="13" fontWeight="600" letterSpacing="2">
        <textPath href="#curveBottom" startOffset="50%" textAnchor="middle">SKIN HEALTH</textPath>
      </text>

      {/* Core */}
      <circle cx="200" cy="200" r="58" fill="#fff" opacity="0.4" />
      <circle cx="200" cy="200" r="44" fill="url(#core)" />
      {/* Medical cross/caduceus icon */}
      <g transform="translate(200,200)">
        <rect x="-3" y="-16" width="6" height="32" fill="#fff" rx="1" />
        <rect x="-14" y="-3" width="28" height="6" fill="#fff" rx="1" />
      </g>
    </svg>
  );
}
